import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TablePagination,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SaleDataService from '../../services/sale.service';
import ProductDataService from '../../services/product.service';
import LocationDataService from '../../services/location.service';
import ClientDataService from '../../services/client.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrencySync, initializeCurrency } from '../../utils/currencyUtils';

const SaleList = () => {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    product_id: '',
    client_id: '',
    location_id: '',
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  useEffect(() => {
    initializeCurrency();
    fetchSales();
    fetchProducts();
    fetchLocations();
    fetchClients();
  }, [page, rowsPerPage, filters]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
      );
      
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...cleanFilters
      };
      
      const response = await SaleDataService.getAll(params);
      const salesData = response.data?.data?.data || [];
      const totalData = response.data?.data?.total || 0;
      
      setSales(salesData);
      setTotal(totalData);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
      setTotal(0);
      showSnackbar(t('sales.errorLoadingSales') + ': ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await ProductDataService.getAll();
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await LocationDataService.getAll();
      setLocations(response.data?.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await ClientDataService.getClients();
      setClients(response.data?.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await SaleDataService.delete(deleteDialog.id);
      showSnackbar(t('sales.saleDeletedSuccess'));
      fetchSales();
    } catch (error) {
      showSnackbar(t('sales.errorDeletingSale'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('sales.title')}</Typography>
            {hasPermission('sales.sell') && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/sales/new')}
              >
                {t('sales.newSale')}
              </Button>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                placeholder={t('sales.search')}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('sales.product')}</InputLabel>
                <Select
                  value={filters.product_id}
                  onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
                  label="Product"
                >
                  <MenuItem value="">{t('sales.allProducts')}</MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('sales.client')}</InputLabel>
                <Select
                  value={filters.client_id}
                  onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
                  label="Client"
                >
                  <MenuItem value="">{t('sales.allClients')}</MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('sales.location')}</InputLabel>
                <Select
                  value={filters.location_id}
                  onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}
                  label="Location"
                >
                  <MenuItem value="">{t('sales.allLocations')}</MenuItem>
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('sales.date')}</TableCell>
                  <TableCell>{t('sales.product')}</TableCell>
                  <TableCell>{t('sales.client')}</TableCell>
                  <TableCell>{t('sales.location')}</TableCell>
                  <TableCell align="right">{t('sales.quantity')}</TableCell>
                  <TableCell align="right">{t('sales.unitPrice')}</TableCell>
                  <TableCell align="right">{t('sales.total')}</TableCell>
                  <TableCell>{t('sales.notes')}</TableCell>
                  <TableCell align="center">{t('sales.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : sales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {t('sales.noSalesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  sales.map((sale) => (
                    <TableRow key={sale.id} hover>
                      <TableCell>
                        {sale.sale_date 
                          ? new Date(sale.sale_date).toLocaleDateString()
                          : new Date(sale.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{sale.product?.name || 'N/A'}</TableCell>
                      <TableCell>
                        {sale.client ? (
                          <Chip label={sale.client.name} size="small" color="primary" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">No client</Typography>
                        )}
                      </TableCell>
                      <TableCell>{sale.location?.name || 'N/A'}</TableCell>
                      <TableCell align="right">{parseFloat(sale.quantity).toFixed(2)}</TableCell>
                      <TableCell align="right">{formatCurrencySync(sale.unit_price || 0)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatCurrencySync(sale.total || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {sale.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sales/${sale.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(sale.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={t('pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => 
              t('pagination.displayedRows', { from, to, count })
            }
          />
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Delete Sale</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this sale? This will also remove the corresponding kardex entry.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SaleList;
